import { useState } from 'react';
import { Conversation } from '@kifuerpflege/shared';
import { useConversationStore } from '../../stores/conversationStore';
import { useUIStore } from '../../stores/uiStore';
import { cn } from '@/lib/utils';
import { MessageSquare, MoreVertical, Pencil, Archive, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogClose } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface ChatItemProps {
  conversation: Conversation;
  isActive: boolean;
}

export function ChatItem({ conversation, isActive }: ChatItemProps) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title);
  const { selectConversation, deleteConversation, updateConversation } = useConversationStore();
  const { setSidebarOpen, setCurrentView } = useUIStore();

  const handleClick = () => {
    selectConversation(conversation.id);
    // Switch to chat view and close sidebar on mobile
    setCurrentView('chat');
    setSidebarOpen(false);
  };

  const handleRename = async () => {
    if (!newTitle.trim() || newTitle === conversation.title) {
      setRenameOpen(false);
      return;
    }

    try {
      await updateConversation(conversation.id, { title: newTitle.trim() });
      setRenameOpen(false);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
  };

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateConversation(conversation.id, { isArchived: true });
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteConversation(conversation.id);
      setDeleteOpen(false);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const openRenameDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNewTitle(conversation.title);
    setRenameOpen(true);
  };

  const openDeleteDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteOpen(true);
  };

  return (
    <>
      <div
        className={cn(
          'group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
        onClick={handleClick}
      >
        <MessageSquare className="h-4 w-4 shrink-0" />

        <div className="flex-1 min-w-0 overflow-hidden" title={conversation.title}>
          <p className="text-sm font-medium truncate">{conversation.title}</p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={openRenameDialog}>
              <Pencil className="mr-2 h-4 w-4" />
              <span>Umbenennen</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleArchive}>
              <Archive className="mr-2 h-4 w-4" />
              <span>Archivieren</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openDeleteDialog} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Löschen</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm">
          <DialogClose onClose={() => setRenameOpen(false)} />
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Chat umbenennen</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="chatTitle">Titel</Label>
                <Input
                  id="chatTitle"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Chat-Titel eingeben"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRename();
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRenameOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleRename}>
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogClose onClose={() => setDeleteOpen(false)} />
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-2">Chat löschen</h2>
            <p className="text-muted-foreground mb-4">
              Möchten Sie den Chat "{conversation.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Löschen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
